import './style/calendar.scss';
import IDate from '@dvrd/idate';
import React, {MouseEventHandler, ReactElement, useContext, useState} from 'react';
import {CalendarEvent, getRenderDates} from "../../../util/calendarUtil";
import {AwesomeIcon, CloseButton, Loader, padNum, WithBackground} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import EditableHeader from "../../editor/editableHeader";
import {AppContext} from "../../context/appContext";

interface Props {
    onNextMonth: MouseEventHandler;
    onPrevMonth: MouseEventHandler;
    onCurrentMonth: MouseEventHandler;
    startDate: IDate;
    endDate: IDate;
    events: Array<CalendarEvent>;
    loading: boolean;
}

export default function Calendar(props: Props) {
    const {calendarOnly} = useContext(AppContext);
    const {onNextMonth, onPrevMonth, onCurrentMonth, startDate, endDate, events, loading} = props;
    const [activeEvent, setActiveEvent] = useState<CalendarEvent | null>(null);

    function onCloseActiveEvent() {
        setActiveEvent(null);
    }

    function onClickEvent(event: CalendarEvent) {
        return function() {
            setActiveEvent(event);
        }
    }

    function getEvents(date: IDate): Array<CalendarEvent> {
        return events.filter((event: CalendarEvent) => date.isAtOrBetween(event.startDate, event.endDate, 'day'));
    }

    function renderHeader() {
        return (
            <div className='calendar-header'>
                <div className='month-controls'>
                    {renderMonthControl('prev')}
                    {renderMonthControl('current')}
                    {renderMonthControl('next')}
                </div>
                <label className='month-label'>{startDate.format('MMMM YYYY')}</label>
                <div/>
            </div>
        )
    }

    function renderMonthControl(controlType: 'prev' | 'current' | 'next') {
        let onClick: MouseEventHandler;
        let content: ReactElement;
        if (controlType === 'prev') {
            onClick = onPrevMonth;
            content = <AwesomeIcon name='arrow-left' className='control-icon' title='Vorige maand'/>
        } else if (controlType === 'current') {
            onClick = onCurrentMonth;
            content = <span className='control-label'>Deze maand</span>
        } else {
            onClick = onNextMonth;
            content = <AwesomeIcon name='arrow-right' className='control-icon' title='Volgende maand'/>
        }
        return (
            <div className={classNames('month-control', controlType)} onClick={onClick}>
                {content}
            </div>
        )
    }

    function renderBody() {
        const dates = getRenderDates(startDate, endDate);
        return (
            <div className='calendar-body'>
                <Loader active={loading} disableBackground/>
                {renderDayLabel('Ma.')}
                {renderDayLabel('Di.')}
                {renderDayLabel('Wo.')}
                {renderDayLabel('Do.')}
                {renderDayLabel('Vr.')}
                {renderDayLabel('Za.')}
                {renderDayLabel('Zo.')}
                {dates.map(renderDate)}
                {(dates.length / 7 < 6) && <div className='calendar-cell hidden'/>}
            </div>
        )
    }

    function renderDayLabel(day: string) {
        return (
            <div className='calendar-cell weekday'>
                <label className='day-label'>{day}</label>
            </div>
        );
    }

    function renderDate(date: IDate) {
        const isOuterDate = date.isBefore(startDate) || date.isAfter(endDate);
        const key = date.format('YYYYMMDD');
        const events = getEvents(date);
        return (
            <div key={key} className={classNames('calendar-cell', isOuterDate && 'outer')}>
                <span className='day-label'>{padNum(date.day())}</span>
                <div className='events-container'>
                    {events.map(renderEvent)}
                </div>
            </div>
        )
    }

    function renderEvent(event: CalendarEvent, index: number) {
        return (
            <div key={index} className='calendar-event' onClick={onClickEvent(event)}>
                <label className='event-label'>{event.summary}</label>
            </div>
        )
    }

    function renderEventPopup() {
        return (
            <WithBackground active={!!activeEvent} onClose={onCloseActiveEvent}>
                <div className='active-event default-popup'>
                    <div className='header-container'>
                        <label className='header-title'>{activeEvent?.summary}</label>
                        <CloseButton onClick={onCloseActiveEvent}/>
                    </div>
                    <div className='body-container'>
                        <p className='event-description'
                           dangerouslySetInnerHTML={{
                               __html: activeEvent?.description.replaceAll('\n', '<br/>') ?? ''
                           }}/>
                    </div>
                </div>
            </WithBackground>
        );
    }

    if (calendarOnly) return (
        <>
            <div className='calendar standalone'>
                {renderHeader()}
                {renderBody()}
            </div>
            {renderEventPopup()}
        </>
    );

    return (
        <>
            <div className='calendar-page default-page'>
                <EditableHeader textKey='calendar-header-1' className='page-title'/>
                <div className='calendar'>
                    {renderHeader()}
                    {renderBody()}
                </div>
            </div>
            {renderEventPopup()}
        </>
    )
}